import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CalendarTodayOutlined } from '@mui/icons-material';

const isDateString = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return (
        /^\d{2}\/\d{2}\/\d{4}$/.test(value.trim()) ||
        /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(value.trim()) ||
        /^\d{4}-\d{2}-\d{2}/.test(value)
    );
};

const STATUS_STYLES: Record<string, { color: string }> = {
    'working on it': { color: '#1e66ab' },
    'sent':{ color: '#96c121' },
    'invoiced': { color: '#6366F1' },
    '3': { color: '#230400' },
    '12': { color: '#850b15' },
    '0': { color: '#96c121' },
};

// const getDaysOverdueColor = (value: string): string => {
//     const days = parseInt(value, 10);
//     if (isNaN(days)) return '#96c121';
//     if (days >= 12) return STATUS_STYLES['12'].color;
//     if (days >= 2)  return STATUS_STYLES['3'].color;
//     return STATUS_STYLES['0'].color;
// };

const capitalize = (text: string) =>
    text.charAt(0).toUpperCase() + text.slice(1);

const isStatusField = (field: string): boolean =>
    /status|balance|Days.*Over/i.test(field);

const hasDotPrefix = (field: string): boolean =>
    /afpStatus|balance/i.test(field);

// const isDaysOverdueField = (field: string): boolean =>
//     /Days.*Over/i.test(field);

interface StatusChipProps {
    value: string;
    color: string;
    showDot: boolean;
}

const StatusChip =React.memo(({ value, color, showDot }: StatusChipProps) => (
    <Tooltip title={value}>
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: `${color}20`, 
                color: color,
                fontWeight: 500,
                fontSize: '14px',
                cursor: 'pointer',
                p: 1.5,
                gap: 1,
                fontFamily: '"Poppins", sans-serif',
                letterSpacing: '0.2px',
                whiteSpace: 'nowrap',
                height: '27px',
                borderRadius: '5px',
                ...(showDot && {
                    '&::before': {
                        content: '""',
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: color,
                        display: 'inline-block',
                        flexShrink: 0,
                    },
                }),
            }}
        >
            {value}
        </Box>
    </Tooltip>
));
StatusChip.displayName = 'StatusChip';

const gridStyles = {
    border: 'none',
    fontFamily: '"Poppins", sans-serif',
    '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #dce6f5' },
    '& .MuiDataGrid-columnHeader': { padding: '0 16px', backgroundColor: 'transparent' },
    '& .MuiDataGrid-columnHeaderTitle': { color: '#1e66ab', fontSize: 18, fontWeight: '600' },
    '& .MuiDataGrid-columnSeparator': { display: 'none !important' },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
    '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        color: '#1e66ab',
        fontSize: 14,
        fontWeight: '500',
        fontFamily: '"Poppins", sans-serif',
        borderBottom: '1px solid #eef2fb',
    },
    '& .MuiDataGrid-cell:focus': { outline: 'none' },
    '& .MuiDataGrid-row': { backgroundColor: '#ffffff', transition: 'background-color 0.15s ease' },
    '& .MuiDataGrid-row:hover': { backgroundColor: '#f0f4ff' },
    '& .MuiDataGrid-virtualScroller': { overflowX: 'auto' },
    '& .MuiDataGrid-overlayWrapper': { minHeight: '80px' },
};

type CustomTableViewProps<T extends object> = {
    data: T[];
    columnsConfig?: Partial<Record<keyof T, string>>;
    title?: string;
    scrollX?: boolean;
};

export default function CustomCommercialDataTable<T extends object>({
    data,
    columnsConfig,
    title,
    scrollX = false,
}: CustomTableViewProps<T>) {
    const maxLength = 35;
    const maxTooltipLength = 500;
    const hasTitle = Boolean(title);


    const hasReleaseInTitle = title ? /release/i.test(title) : false;
    const titlePanelBg = hasReleaseInTitle ? '#96c12120' : '#f0f4ff';
    const titlePanelTextColor = hasReleaseInTitle ? '#96c121' : '#1e66ab';

    const columnKeys = React.useMemo<(keyof T)[]>(() => {
        if (!data || data.length === 0) return [];
        return Object.keys(data[0]) as (keyof T)[];
    }, [data]);

    const columns = React.useMemo<GridColDef[]>(() => {
        return columnKeys.map((key) => {
            const headerName = columnsConfig?.[key] ?? capitalize(key as string);

            return {
                field: key as string,
                headerName,
                flex: 1,
                minWidth: 120,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    const raw = params.value;
                    const value = raw !== null && raw !== undefined ? String(raw) : '';

                    if (isStatusField(params.field) && value) {
                        const color = STATUS_STYLES[value.toLowerCase()]?.color ?? '#495057';
                        // let color: string;
                
                        //  if (isDaysOverdueField(params.field)) {
                        //     color = getDaysOverdueColor(value);
                        // } else {
                        //     color = STATUS_STYLES[value.toLowerCase()]?.color ?? '#495057';
                        // }

                        return (
                            <StatusChip
                                value={value}
                                color={color}
                                showDot={hasDotPrefix(params.field)}
                            />
                        );
                    }

                    const isDate = isDateString(value);
                    const isLong = value.length > maxLength;
                    const display = isLong ? value.substring(0, maxLength) + '…' : value;
                    const tooltip = value.length > maxTooltipLength ? value.substring(0, maxTooltipLength) + '…' : value;

                    return (
                        <Box
                            title={tooltip}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                width: '100%',
                                overflow: 'hidden',
                            }}
                        >
                            {isDate && (
                                <CalendarTodayOutlined
                                    sx={{ fontSize: '13px', color: '#1e66ab', flexShrink: 0 }}
                                />
                            )}
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {display}
                            </span>
                        </Box>
                    );
                },
            };
        });
    }, [columnKeys, columnsConfig]);

    const rows = React.useMemo(() => data.map((row, idx) => ({ id: idx, ...row })), [data]);

    const headerBg = hasTitle ? 'transparent' : '#f0f4ff';
    const conditionalGridStyles = {
        ...gridStyles,
        '& .MuiDataGrid-columnHeaders': { ...gridStyles['& .MuiDataGrid-columnHeaders'], backgroundColor: headerBg },
        '& .MuiDataGrid-columnHeader': { ...gridStyles['& .MuiDataGrid-columnHeader'], backgroundColor: `${headerBg} !important` },
        '& .MuiDataGrid-filler': { backgroundColor: `${headerBg} !important` },
        '& .MuiDataGrid-scrollbarFiller': { backgroundColor: `${headerBg} !important` },
    };

    return (
        <Box
            sx={{
                borderRadius: '14px',
                overflow: 'hidden',
                border: '1px solid #dce6f5',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                boxShadow: '0px 2px 8px rgba(30, 102, 171, 0.08)',
            }}
        >
            {hasTitle && (
                <Box sx={{ backgroundColor: titlePanelBg, px: 2, py: 2, borderBottom: '1px solid #c5d9ef' }}>
                    <Typography
                        sx={{
                            fontFamily: '"Poppins", sans-serif',
                            fontWeight: 700,
                            fontSize: 18,
                            color: titlePanelTextColor,
                            letterSpacing: '0.6px',
                            textTransform: 'uppercase',
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
            )}

            <Box sx={{ flex: 1, minHeight: 0 }}>
                <DataGrid
                    sx={conditionalGridStyles}
                    rows={rows}
                    columns={columns}
                    rowHeight={46}
                    columnHeaderHeight={48}
                    disableRowSelectionOnClick
                    hideFooter
                    autoHeight={!hasTitle}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    slots={{
                        noRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                                <Typography sx={{ color: '#1e66ab', fontSize: '13px', fontFamily: '"Poppins", sans-serif' }}>
                                    No data available.
                                </Typography>
                            </Box>
                        ),
                    }}
                />
            </Box>
        </Box>
    );
}